import { z } from 'zod';
import { defineListQuery, listFilter } from './list-query.schemas';
import { postTripPayloadSchema, type PostTripDefect } from './post-trip.schemas';
import type { WorkOrderSeverity } from './work-order.schemas';

/** The states an unreceived trip can be in while its truck is on the way back: delivered, or still going with the driver's post-trip check already in. */
export const EXPECTED_RETURN_STATUSES = ['dispatched', 'in_transit', 'exception', 'delivered'] as const;
export const EXPECTED_RETURN_CHECKS = ['recorded', 'not_recorded'] as const;

/** `GET /yard/expected-returns` (gitaumoses4/chukua#108): trucks due back at the yard, the longest-waiting first. */
export const expectedReturnsQuerySchema = defineListQuery({
  sortFields: ['number', 'deliveredAt'],
  defaultSort: { sort: 'deliveredAt', order: 'asc' },
  filters: {
    status: listFilter(z.enum(EXPECTED_RETURN_STATUSES)),
    check: listFilter(z.enum(EXPECTED_RETURN_CHECKS)),
  },
});
export type ExpectedReturnsQuery = z.infer<typeof expectedReturnsQuerySchema>;

export interface ExpectedReturnItem {
  id: string;
  /** `T-<n>`. */
  number: string;
  status: string;
  jobNumber: string | null;
  customerName: string | null;
  truckRegistration: string | null;
  driverName: string | null;
  /** When proof of delivery was captured (server time); null while the trip is still going. */
  deliveredAt: string | null;
  hasPostTripCheck: boolean;
}

/**
 * `POST /yard/trips/:id/receive` (gitaumoses4/chukua#108). The yard confirms the driver's readings or, where the driver recorded none, enters them;
 * a reading sent here replaces the driver's, as a new event that corrects it. Nothing sent means the check on the trip stands.
 */
export const receiveTruckInputSchema = postTripPayloadSchema.pick({ odometerKm: true, fuelLevelPercent: true }).partial().strict();
export type ReceiveTruckInput = z.infer<typeof receiveTruckInputSchema>;

export const YARD_RECEIPT_MISSING_CODES = ['proof_of_delivery', 'odometer', 'fuel_level'] as const;
export type YardReceiptMissingCode = (typeof YARD_RECEIPT_MISSING_CODES)[number];

/** The wording of each, shared so the screen's own check, made as the supervisor types a reading, reads the same as the API's 409. */
export const YARD_RECEIPT_MISSING_MESSAGES: Record<YardReceiptMissingCode, string> = {
  proof_of_delivery: 'Proof of delivery has not been captured',
  odometer: 'The final odometer reading is missing',
  fuel_level: 'The final fuel reading is missing',
};

/** What stops a receipt, worded for the person at the yard; the screen shows these as the API sent them. */
export interface YardReceiptMissingItem {
  code: YardReceiptMissingCode;
  message: string;
}

/** `ready`: Receive will succeed. `blocked`: something is missing and can be supplied. `refused`: this trip cannot be received. `received`: it already was. */
export type YardReceiptVerdict = 'ready' | 'blocked' | 'refused' | 'received';

export interface YardReceiptCheckView {
  odometerKm: number | null;
  fuelLevelPercent: number | null;
  tyreCondition: string | null;
  bodyDamage: string | null;
  note: string | null;
  defects: PostTripDefect[];
  photoCount: number;
  /** Who wrote the standing check: the driver's phone, or the yard for them. */
  recordedBy: 'driver' | 'office';
}

export interface ReceivedWorkOrderView {
  id: string;
  sequenceNumber: number;
  severity: WorkOrderSeverity;
  title: string;
}

export interface ReceiveResult {
  tripId: string;
  number: string;
  truckRegistration: string | null;
  receivedAt: string;
  odometerKm: number;
  fuelLevelPercent: number;
  workOrders: ReceivedWorkOrderView[];
}

export interface YardReceiptView {
  tripId: string;
  number: string;
  status: string;
  jobNumber: string | null;
  customerName: string | null;
  truckRegistration: string | null;
  driverName: string | null;
  verdict: YardReceiptVerdict;
  missing: YardReceiptMissingItem[];
  refusal: string | null;
  /** Things worth a look that do not stop the receipt, such as a defect reported with no photo. */
  warnings: string[];
  check: YardReceiptCheckView | null;
  received: ReceiveResult | null;
}
